import './style/defaultVatDialog.scss';
import {ErrorType} from '@dvrd/dvr-controls';
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {getVatItemsByCountry} from '../../../util/countryUtil';
import {VATValue} from '../../../util/interfaces';
import {PortalSelectItem} from '../../../util/types';
import {CountryVATValues} from '../../../util/vatUtil';
import {BluemateDialog, BluemateVatCountryRow} from '../../controls';
import PortalSelectController from '../../controls/portalSelect/portalSelectController';

interface Props {
    onClose: VoidFunction;
    onAddCountry: (countryCode: string) => void;
    onDeleteCountry: (countryCode: string) => VoidFunction;
    onClickValue: (countryCode: string, value: VATValue) => VoidFunction;
    onChangeCustom: (countryCode: string) => (value: string) => void;
    onSubmit: VoidFunction;
    active: boolean;
    countryCodes: Array<string>;
    availableCountries: Array<[string, string]>;
    draft: CountryVATValues;
    errors: Record<string, ErrorType>;
    canEdit: boolean;
    dirty: boolean;
    saving: boolean;
}

export default function DefaultVatDialog(props: Props) {
    const {
        onClose,
        onAddCountry,
        onDeleteCountry,
        onClickValue,
        onChangeCustom,
        onSubmit,
        active,
        countryCodes,
        availableCountries,
        draft,
        errors,
        canEdit,
        dirty,
        saving
    } = props;
    const {t} = useTranslation();
    const countryItems: Array<PortalSelectItem> = useMemo(
        () => availableCountries.map((entry: [string, string]) => ({value: entry[0], label: entry[1]})),
        [availableCountries]
    );

    function renderCountryRow(countryCode: string) {
        return (
            <BluemateVatCountryRow
                key={countryCode}
                countryCode={countryCode}
                value={draft.getCountryValue(countryCode)}
                options={getVatItemsByCountry(countryCode, undefined, false)}
                onSelect={onSelectValue}
                onChangeCustom={onChangeCustom(countryCode)}
                onDelete={canEdit ? onDeleteCountry(countryCode) : undefined}
                error={errors[countryCode]}
                disabled={!canEdit || saving}
            />
        );
    }

    function onSelectValue(countryCode: string, value: VATValue) {
        onClickValue(countryCode, value)();
    }

    return (
        <BluemateDialog
            active={active}
            onClose={onClose}
            title={t('products.defaultVat.optionsTitle', 'Standaard BTW tarieven instellen')}
            onConfirm={onSubmit}
            confirmLabel={t('products.defaultVat.save', 'Opslaan')}
            cancelLabel={t('products.defaultVat.cancel', 'Annuleren')}
            cancelVariant='tertiary'
            confirmDisabled={!canEdit || saving}
            loading={saving}
            className='products-default-vat-dialog'
            preventBackgroundClick={dirty}
            escapeDisabled={dirty}
        >
            <div className='bluemate-vat-country-rows'>{countryCodes.map(renderCountryRow)}</div>
            {canEdit && !!availableCountries.length && (
                <div className='add-country'>
                    <label>{t('settings.vat.addDefaultFor', 'Standaard BTW tarief toevoegen voor:')}</label>
                    <PortalSelectController
                        onChange={(value: string | null) => onAddCountry(value ?? '')}
                        value=''
                        items={countryItems}
                        placeholder={t('settings.vat.chooseCountry', 'Kies een land')}
                        shape='rounded'
                        uniformFont
                        noShadow
                        itemsHeight='20rem'
                    />
                </div>
            )}
        </BluemateDialog>
    );
}
